import { useState, useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import { REALMS, REALM_IDS, MODES, MODE_IDS, BUCKETS, BUCKET_IDS, bucketChallenge } from '@wot/shared';
import type { ModeId } from '@wot/shared';
import { RealmIcon } from '../components/RealmIcon';

// ── Local design tokens (SIGNAL) ─────────────────────────────────
const VIOLET = '#3E7BFA';
const CORAL = '#F43F5E';

const label = {
  fontSize: '0.82rem',
  fontFamily: 'var(--font-mono)',
  color: '#c6c6ce',
  letterSpacing: '0.2em',
  textAlign: 'center' as const,
};

// One selection language for every control on this screen.
const selectable = (on: boolean, accent: string = VIOLET) => ({
  background: on ? `${accent}1a` : '#16223A',
  border: on ? `1px solid ${accent}` : '1px solid #33415E',
  color: on ? '#fff' : '#c4c4cc',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  fontFamily: 'var(--font-heading)',
  fontWeight: 800,
  letterSpacing: '0.08em',
});

// ─── How hard a realm plays ─────────────────────────────────────────
// Three bars, filled to the realm's level, tinted from green to red. The count
// carries the meaning on its own, so the colour is reinforcement rather than
// the message: a player who cannot separate the hues still sees one bar versus
// three. Measured figures and the reason for the thresholds live in realms.ts.
const CHALLENGE_HUE = ['#14b8a6', '#c4973a', '#e0575b'];
const CHALLENGE_WORD = ['APPROACHABLE', 'TESTING', 'TOUGH'];

function ChallengeMark({ level, dim }: { level: 1 | 2 | 3; dim: boolean }) {
  const hue = CHALLENGE_HUE[level - 1];
  return (
    <span
      title={`${CHALLENGE_WORD[level - 1]}. Players get roughly ${[65, 52, 43][level - 1]} per cent of the easy questions right in this realm.`}
      style={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexShrink: 0, height: 13 }}
    >
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            width: 3,
            height: 5 + i * 4,
            borderRadius: 1,
            background: i < level ? hue : '#33415E',
            opacity: i < level && dim ? 0.55 : 1,
            transition: 'opacity 0.15s ease',
          }}
        />
      ))}
    </span>
  );
}

import { MODE_STYLE } from '../lib/modeStyle';

export function ConfigScreen() {
  const gameMode = useGameStore((s) => s.gameMode);
  const selectedRealm = useGameStore((s) => s.selectedRealm);
  const setSelectedRealm = useGameStore((s) => s.setSelectedRealm);
  const selectedMode = useGameStore((s) => s.selectedMode);
  const setSelectedMode = useGameStore((s) => s.setSelectedMode);
  const startSoloMatch = useGameStore((s) => s.startSoloMatch);
  const createRoom = useGameStore((s) => s.createRoom);
  const setView = useGameStore((s) => s.setView);

  const [loading, setLoading] = useState(false);
  // Custom stays folded until asked for: four arenas and ALL are the offer,
  // the ten-realm grid is the power user's escape hatch underneath.
  const [customOpen, setCustomOpen] = useState(
    () => selectedRealm !== 'ALL' && !(BUCKET_IDS as readonly string[]).includes(selectedRealm));
  const realmCounts = useGameStore((s) => s.realmCounts);
  const countsLoaded = useGameStore((s) => s.realmCountsLoaded);
  const fetchRealmCounts = useGameStore((s) => s.fetchRealmCounts);

  // Counts are prefetched at app start; this is only a fallback (deep links).
  useEffect(() => {
    if (!countsLoaded) fetchRealmCounts();
  }, [countsLoaded]);

  const isSolo = gameMode === 'solo';
  const isCreateRoom = gameMode === 'create_room';

  // Rooms cannot play SURVIVAL (no versus design for an endless game); if the
  // player arrives here with it selected, fall back to the ranked default.
  useEffect(() => {
    if (isCreateRoom && selectedMode === 'SURVIVAL') setSelectedMode('CLIMB');
  }, [isCreateRoom, selectedMode]);

  const modeChoices = MODE_IDS.filter(id => !(isCreateRoom && id === 'SURVIVAL'));

  const totalQuestions = Object.values(realmCounts).reduce((sum, c) => sum + c.total, 0);
  const arenaPool = selectedRealm === 'ALL'
    ? totalQuestions
    : (BUCKET_IDS as readonly string[]).includes(selectedRealm)
      ? BUCKETS[selectedRealm as keyof typeof BUCKETS].realms
          .reduce((sum, r) => sum + (realmCounts[r]?.total || 0), 0)
      : (realmCounts[selectedRealm]?.total || 0);

  async function handleStart() {
    setLoading(true);
    try {
      if (isSolo) {
        await startSoloMatch(selectedRealm);
      } else if (isCreateRoom) {
        await createRoom();
      }
    } finally {
      setLoading(false);
    }
  }

  const availableRealms = REALM_IDS.filter(id => (realmCounts[id]?.total || 0) > 0);
  const isSurvival = selectedMode === 'SURVIVAL';
  // Mouse drag-to-scroll for the realm strip: touch scrolls natively on the
  // phone, but on the web the strip must follow a held mouse. A real drag
  // (>5px) swallows the click so releasing over a card does not select it.
  const stripRef = useRef<HTMLDivElement>(null);
  const stripDrag = useRef({ down: false, x: 0, left: 0, moved: false });
  // The start button wears the selected mode's own color (MODE_STYLE).
  const accent = MODE_STYLE[selectedMode]?.color ?? VIOLET;

  return (
    <div className="page-realms" style={{ padding: '6px 0 88px', gap: 0 }}>
      {/* Single 400px column: every control shares the same edges. */}
      <div style={{
        width: '100%', maxWidth: 400, margin: '0 auto', padding: '0 16px',
        display: 'flex', flexDirection: 'column', gap: 12, flex: 1, justifyContent: 'center',
      }}>

        {/* ── HEADER: back + title on one row ── */}
        <div style={{ display: 'flex', alignItems: 'center', height: 40 }}>
          <button
            onClick={() => setView('home')}
            aria-label="Back"
            style={{
              width: 48, height: 48, flexShrink: 0,
              background: '#20304E', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 12,
              color: '#e8e8ea', fontSize: '1.35rem', cursor: 'pointer', lineHeight: 1,
            }}
          >
            &#8592;
          </button>
          <h1 style={{
            flex: 1, textAlign: 'center', margin: 0,
            fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
            fontSize: '1.25rem', letterSpacing: '-0.02em', color: '#fff',
          }}>
            {isSolo ? 'SOLO SETUP' : 'ROOM SETUP'}
          </h1>
          <div style={{ width: 48, flexShrink: 0 }} />
        </div>

        {/* ── MODE ──
            Replaces the old difficulty and question-count pickers. A player
            chooses a mood; the recipe (counts, bands, clocks) is fixed per
            mode so scores compare and the brutal hard band is opt-in. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={label}>MODE</span>
          {/* Full-width rows; each mode wears its own color and icon so the
              row reads as a game button, not a settings paragraph. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {modeChoices.map(id => {
              const on = selectedMode === id;
              const M = MODE_STYLE[id];
              return (
                <button
                  key={id}
                  onClick={() => setSelectedMode(id)}
                  style={{
                    minHeight: 68, padding: '10px 16px 10px 16px',
                    display: 'flex', alignItems: 'center', gap: 14,
                    textAlign: 'left', cursor: 'pointer', position: 'relative',
                    borderRadius: 12,
                    border: `2px solid ${on ? M.bright : 'rgba(255,255,255,0.14)'}`,
                    background: on
                      ? `linear-gradient(120deg, ${M.color}33 0%, ${M.color}14 55%, transparent 100%), #1C2A45`
                      : `linear-gradient(120deg, ${M.color}1F 0%, transparent 55%), #16223A`,
                    boxShadow: on
                      ? `0 5px 0 ${M.color}66, 0 0 22px ${M.color}55, inset 0 0 18px ${M.color}22`
                      : '0 5px 0 #0D1626',
                    // no inline transform: it would block the global
                    // button:active press dip; the glow marks selection.
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span style={{
                    width: 48, height: 48, borderRadius: 13, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${M.color}${on ? '3D' : '24'}`,
                    border: `1px solid ${M.color}${on ? '88' : '44'}`,
                  }}>
                    <svg viewBox="0 0 24 24" width="28" height="28" fill={on ? M.bright : M.color}>
                      <path d={M.icon} />
                    </svg>
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span style={{
                      fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
                      fontSize: '1.25rem', letterSpacing: '0.04em', lineHeight: 1,
                      color: on ? M.bright : '#fff',
                    }}>
                      {MODES[id].name.toUpperCase()}
                    </span>
                    <span style={{
                      fontFamily: 'var(--font-sans)', fontWeight: 600,
                      fontSize: 'clamp(0.78rem, 3.8vw, 0.9rem)', letterSpacing: '0.01em', lineHeight: 1.25,
                      color: on ? '#ECECF2' : '#B8B8CA',
                      // One line at every normal font scale; at a large system
                      // font on a narrow phone it wraps rather than truncates.
                      overflowWrap: 'anywhere',
                    }}>
                      {M.line}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── ARENA ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={label}>ARENA</span>
          <button
            onClick={() => setSelectedRealm('ALL')}
            style={{
              ...selectable(selectedRealm === 'ALL'),
              width: '100%', minHeight: 56, fontSize: '1rem', fontWeight: 900,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
              borderRadius: 12,
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>ALL REALMS</span>
            <span style={{
              fontSize: '0.8rem', fontFamily: 'var(--font-sans)', fontWeight: 600, letterSpacing: 0,
              color: selectedRealm === 'ALL' ? '#A9C7FF' : '#B8B8CA', whiteSpace: 'nowrap',
            }}>
              every subject
            </span>
          </button>

          {/* Four curated arenas. Identity comes from the member realms: the
              stripe and icons are theirs, the bucket invents no new colour. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 6 }}>
            {BUCKET_IDS.map(id => {
              const b = BUCKETS[id];
              const on = selectedRealm === id;
              return (
                <button
                  key={id}
                  onClick={() => setSelectedRealm(on ? 'ALL' : id)}
                  style={{
                    minHeight: 70, padding: '8px 12px',
                    background: on
                      ? `linear-gradient(130deg, ${b.color}30 0%, transparent 70%), #1C2A45`
                      : `linear-gradient(130deg, ${b.color}1A 0%, transparent 60%), #16223A`,
                    border: `2px solid ${on ? b.color : 'rgba(255,255,255,0.12)'}`,
                    borderRadius: 14,
                    boxShadow: on ? `0 5px 0 ${b.color}55, 0 0 16px ${b.color}44` : '0 5px 0 #0D1626',
                    color: on ? '#fff' : '#D0D0DC',
                    display: 'flex', alignItems: 'center', gap: 8,
                    cursor: 'pointer', transition: 'all 0.15s ease',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
                    <span style={{
                      fontFamily: 'var(--font-heading)', fontWeight: 800, fontStyle: 'italic',
                      fontSize: '0.95rem', letterSpacing: '0.05em', lineHeight: 1,
                      color: on ? b.color : '#fff',
                    }}>{b.name.toUpperCase()}</span>
                    <span style={{
                      fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: '0.8rem',
                      lineHeight: 1.25, color: on ? '#ECECF2' : '#A8A8BC',
                    }}>{b.tagline}</span>
                  </span>
                  <ChallengeMark level={bucketChallenge(id)} dim={!on} />
                </button>
              );
            })}
          </div>

          {/* The ten-realm grid, folded behind one quiet line. Single-subject
              play is for studying a realm, not the default offer. */}
          {/* Was a bare text line; nobody read it as pressable. A real
              bordered control at thumb size, with a direction glyph. */}
          <button
            onClick={() => setCustomOpen(o => !o)}
            style={{
              width: '100%', height: 44,
              background: '#16223A', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
              fontFamily: 'var(--font-mono)', fontSize: '0.8rem', fontWeight: 700,
              letterSpacing: '0.14em', color: '#cdcdd3',
            }}
          >
            {customOpen ? 'HIDE SINGLE REALMS' : 'PICK A SINGLE REALM'}
            <span style={{ fontSize: '0.8rem', color: '#c6c6ce' }}>{customOpen ? '▲' : '▼'}</span>
          </button>

          {/* A swipeable strip, not a ten-box wall: realms are browsed
              sideways like a carousel, with snap stops per card. */}
          {customOpen && (
            <div
              className="realm-strip"
              ref={stripRef}
              onPointerDown={(e) => {
                stripDrag.current = { down: true, x: e.clientX, left: stripRef.current?.scrollLeft ?? 0, moved: false };
              }}
              onPointerMove={(e) => {
                const d = stripDrag.current;
                if (!d.down || !stripRef.current) return;
                const dx = e.clientX - d.x;
                if (Math.abs(dx) > 5) d.moved = true;
                stripRef.current.scrollLeft = d.left - dx;
              }}
              onPointerUp={() => { stripDrag.current.down = false; }}
              onPointerLeave={() => { stripDrag.current.down = false; }}
              onClickCapture={(e) => {
                if (stripDrag.current.moved) {
                  e.preventDefault();
                  e.stopPropagation();
                  stripDrag.current.moved = false;
                }
              }}
              onWheel={(e) => {
                if (stripRef.current && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                  stripRef.current.scrollLeft += e.deltaY;
                }
              }}
              style={{
                display: 'flex', gap: 10, overflowX: 'auto',
                scrollSnapType: 'x proximity', WebkitOverflowScrolling: 'touch',
                padding: '4px 2px 8px', cursor: 'grab',
              }}
            >
              {availableRealms.map((id) => {
                const realm = REALMS[id];
                const on = selectedRealm === id;
                return (
                  <button
                    key={id}
                    onClick={() => setSelectedRealm(on ? 'ALL' : id)}
                    style={{
                      // min, not fixed: Android font scaling inflates rem
                      // sizes, and a fixed box clips the second name line
                      // exactly on the devices that need it most.
                      minHeight: 62, width: 168, flex: '0 0 auto', scrollSnapAlign: 'start',
                      padding: '8px 11px',
                      background: on
                        ? `linear-gradient(130deg, ${realm.color}30 0%, transparent 70%), #1C2A45`
                        : `linear-gradient(130deg, ${realm.color}14 0%, transparent 60%), #16223A`,
                      border: `2px solid ${on ? realm.color : 'rgba(255,255,255,0.12)'}`,
                      borderRadius: 12,
                      boxShadow: on ? `0 5px 0 ${realm.color}55, 0 0 14px ${realm.color}44` : '0 5px 0 #0D1626',
                      color: on ? '#fff' : '#D0D0DC',
                      display: 'flex', alignItems: 'center', gap: 9,
                      cursor: 'pointer', transition: 'all 0.15s ease',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ display: 'flex', flexShrink: 0 }}>
                      <RealmIcon id={id} size={19} color={realm.color} />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                      <span style={{
                        fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 800,
                        fontSize: '0.9rem', letterSpacing: '0.05em', lineHeight: 1,
                        color: on ? realm.color : '#fff',
                      }}>{id}</span>
                      {/* Wraps to a second line instead of ellipsizing: an
                          ellipsis was eating the second word, the half that
                          says what the realm actually is. */}
                      <span style={{
                        fontFamily: 'var(--font-sans)', fontWeight: 500,
                        fontSize: '0.8rem', lineHeight: 1.2,
                        color: on ? '#ECECF2' : '#A8A8BC',
                        overflow: 'hidden',
                        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                      }}>{realm.name}</span>
                    </span>
                    <span style={{ display: 'flex', flexShrink: 0 }}>
                      <ChallengeMark level={realm.challenge} dim={!on} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Without this the bars are decoration. One line is enough: the mark
              only has to be legible, not explained in full. */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            marginTop: 2, fontFamily: 'var(--font-sans)', fontSize: '0.8rem',
            color: '#A8A8BC',
          }}>
            <ChallengeMark level={1} dim={false} />
            <span>easier</span>
            <span style={{ opacity: 0.5 }}>to</span>
            <ChallengeMark level={3} dim={false} />
            <span>harder</span>
          </div>
        </div>

        {/* ── START ── */}
        <button
          onClick={handleStart}
          disabled={loading || (countsLoaded && arenaPool < 5)}
          style={{
            width: '100%', height: 56, marginTop: 2,
            background: `linear-gradient(180deg, ${accent} 0%, ${accent}D8 100%)`,
            border: 'none',
            borderRadius: 16,
            color: '#0A1222',
            boxShadow: `0 6px 0 ${accent}55, 0 10px 26px ${accent}44`,
            fontFamily: 'var(--font-heading)', fontWeight: 900,
            fontSize: '1.05rem', letterSpacing: '0.16em',
            cursor: 'pointer', transition: 'all 0.15s ease',
            opacity: loading || (countsLoaded && arenaPool < 5) ? 0.4 : 1,
          }}
        >
          {loading
            ? 'LOADING'
            : isSolo
              ? `START ${MODES[selectedMode].name.toUpperCase()}`
              : 'CREATE ROOM'}
        </button>

        {countsLoaded && arenaPool < 5 && (
          <p style={{
            textAlign: 'center', fontSize: '0.85rem', color: CORAL,
            fontFamily: 'var(--font-mono)', margin: 0,
          }}>
            Not enough questions ({arenaPool}). Need at least 5.
          </p>
        )}
      </div>
    </div>
  );
}
